import React, { useState, useEffect, useRef, useContext } from "react";
import { GameContext } from "@/contexts/GameContext";
import { X, Sparkles, MessageSquare, Heart, HelpCircle, Compass, ArrowLeftRight } from "lucide-react";

const NYX_QUOTES = {
  it: {
    idle: [
      "Le storie sono infinite. Andiamo a scriverne una insieme?",
      "Hai controllato il tuo zaino oggi? C'è sempre spazio per qualche pozione.",
      "Un passo alla volta, avventuriero. La magia richiede tempo.",
      "Sento una forte energia arcana nell'aria...",
      "Cosa succederà nel prossimo capitolo?",
      "Hai provato ad evolvere la tua classe dalle statistiche? Diventerai fortissimo!",
      "Ricorda che puoi scambiare le tue carte doppie con altri giocatori!"
    ],
    lowHp: [
      "Attenzione! La tua salute è bassa! Dovresti usare una pozione di cura!",
      "PV critici! Non vorrai farti sconfiggere proprio adesso, vero? Usa una carta di cura o bevi una pozione!",
      "Presto, proteggiti! Il prossimo colpo potrebbe essere letale."
    ],
    battle: [
      "Canalizza la tua magia! Sconfiggiamo questo nemico!",
      "Usa le combo giuste per infliggere più danni! Hai provato Doppio Colpo o Scarica Arcana?",
      "Mantieni alta la difesa! La parata è fondamentale quanto l'attacco.",
      "Sento il vento della vittoria soffiare a nostro favore!"
    ],
    highGold: [
      "Che bel bottino d'oro! Facciamo un salto al Negozio per comprare qualche carta o pozione?",
      "Tutto quest'oro fa quasi invidia a un drago! Non accumularlo e basta, usalo per potenziarti!"
    ],
    welcome: [
      "Ciao! Sono Nyx, la tua Guida Arcana. Ti aiuterò nel tuo cammino!",
      "Che la fortuna ti assista in questo viaggio magico!"
    ]
  },
  en: {
    idle: [
      "Stories are infinite. Shall we go write one together?",
      "Have you checked your backpack today? There's always room for some potions.",
      "One step at a time, adventurer. Magic takes time.",
      "I feel a strong arcane energy in the air...",
      "What will happen in the next chapter?",
      "Have you tried evolving your class in the statistics tab? You'll get much stronger!",
      "Remember that you can trade your duplicate cards with other players!"
    ],
    lowHp: [
      "Watch out! Your health is low! You should use a healing potion!",
      "Critical HP! You don't want to get defeated right now, do you? Heal up or use a potion!",
      "Quick, defend yourself! The next hit could be fatal."
    ],
    battle: [
      "Channel your magic! Let's defeat this enemy!",
      "Use the right combos to deal more damage! Have you tried Double Strike or Arcane Barrage?",
      "Keep your defense high! Blocking is as crucial as attacking.",
      "I feel the winds of victory blowing in our favor!"
    ],
    highGold: [
      "What a nice hoard of gold! Shall we pay a visit to the Shop to buy some cards or potions?",
      "All this gold is almost enough to make a dragon jealous! Don't just hoarde it, spend it to grow stronger!"
    ],
    welcome: [
      "Hello! I am Nyx, your Arcane Guide. I will assist you on your journey!",
      "May fortune favor you on this magical adventure!"
    ]
  }
};

const PET_REACTIONS = {
  it: [
    "Fufufu... Grazie, avventuriero! La tua gentilezza riscalda il mio nucleo magico.",
    "Che sensazione piacevole! Sento le mie ali ricaricarsi d'energia.",
    "Le carezze di un grande mago sono sempre gradite!",
    "Hihi! Questo solletica un po', ma mi piace!"
  ],
  en: [
    "Fufufu... Thank you, adventurer! Your kindness warms my magical core.",
    "What a pleasant feeling! I feel my wings charging up with energy.",
    "A great wizard's pets are always welcome!",
    "Hehe! That tickles a bit, but I like it!"
  ]
};

const RIDDLES = {
  it: [
    {
      question: "Canalizzo poteri arcani, ma senza mana sono solo carta dipinta. Cosa sono?",
      options: ["Una pergamena", "Una carta incantesimo", "Un tomo di magia"],
      answer: 1,
      rewardText: "Esatto! Le carte consumano il tuo mana prezioso per compiere prodigi."
    },
    {
      question: "Rinasco dalle mie stesse ceneri per difenderti dal male. Chi sono?",
      options: ["Il Cucciolo di Fenice", "Un Golem di Fango", "L'Eroe Immortale"],
      answer: 0,
      rewardText: "Corretto! La Fenice risorge splendente e pronta a combattere."
    },
    {
      question: "Consumo la tua mente per scagliare incantesimi. Senza di me, la tua magia tace. Cosa sono?",
      options: ["L'Oro", "I Punti Vita", "Il Mana"],
      answer: 2,
      rewardText: "Giusto! Il Mana è la linfa vitale di ogni incantatore."
    },
    {
      question: "Più ne prendi dal mazzo, più ne lasci indietro nel grimorio. Cosa sono?",
      options: ["Le monete d'oro", "Le carte giocate", "Le ferite subite"],
      answer: 1,
      rewardText: "Indovinato! Ogni carta giocata si sposta nel cimitero o sul campo."
    }
  ],
  en: [
    {
      question: "I channel arcane powers, but without mana I am just painted paper. What am I?",
      options: ["A scroll", "A spell card", "A magic tome"],
      answer: 1,
      rewardText: "Exactly! Cards consume your precious mana to perform wonders."
    },
    {
      question: "I rise from my own ashes to defend you from evil. Who am I?",
      options: ["The Phoenix Hatchling", "A Mud Golem", "The Deathless Hero"],
      answer: 0,
      rewardText: "Correct! The Phoenix rises radiant and ready to fight."
    },
    {
      question: "I consume your mind to cast spells. Without me, your magic is silent. What am I?",
      options: ["Gold", "Hit Points", "Mana"],
      answer: 2,
      rewardText: "Right! Mana is the lifeblood of every spellcaster."
    },
    {
      question: "The more cards you draw, the more you leave behind in the deck. What are they?",
      options: ["Gold coins", "Played cards", "Received wounds"],
      answer: 1,
      rewardText: "Spot on! Every card played leaves the deck to enter the field or discard pile."
    }
  ]
};

const TAROT_CARDS = {
  it: [
    { name: "Il Mago", icon: "✨", desc: "La tua energia arcana è all'apice. Oggi i tuoi incantesimi risuoneranno con forza immensa." },
    { name: "La Stella", icon: "⭐", desc: "Un sentiero luminoso si svela tra le ombre. Troverai la via per superare ogni ostacolo." },
    { name: "La Torre", icon: "🏰", desc: "Vecchie certezze crollano per far spazio a nuove opportunità. Il cambiamento è inevitabile." },
    { name: "Il Drago", icon: "🐉", desc: "Una brama di ricchezza pervade la giornata. Oro e tesori ti attendono, ma attento alle fiamme della cupidigia." },
    { name: "La Luna", icon: "🌙", desc: "Le apparenze ingannano. Guarda oltre le ombre per scoprire la verità celata nei segreti." },
    { name: "Il Sole", icon: "☀️", desc: "Chiarezza e vitalità ripristinano le tue forze. I tuoi punti vita e il tuo spirito risplendono." }
  ],
  en: [
    { name: "The Magician", icon: "✨", desc: "Your arcane energy is at its peak. Today your spells will resonate with immense power." },
    { name: "The Star", icon: "⭐", desc: "A bright path reveals itself in the shadows. You will find the way to overcome any obstacle." },
    { name: "The Tower", icon: "🏰", desc: "Old certainties crumble to make room for new opportunities. Change is inevitable." },
    { name: "The Dragon", icon: "🐉", desc: "A hunger for wealth fills the day. Gold and treasures await you, but watch out for the flames of greed." },
    { name: "The Moon", icon: "🌙", desc: "Appearances can be deceiving. Look beyond the shadows to discover the truth hidden in secrets." },
    { name: "The Sun", icon: "☀️", desc: "Clarity and vitality restore your strength. Your health and your spirit shine bright." }
  ]
};

const LORE_SECRETS = {
  it: [
    "Sapevi che il Cucciolo di Fenice è nato dal fuoco primordiale e rinasce con più forza ogni volta che viene sconfitto?",
    "Le carte combo come 'Scarica Arcana' o 'Doppio Colpo' infliggono danni immensi se hai accumulato abbastanza mana.",
    "Si dice che nei capitoli più avanzati si nascondano boss immuni ai danni fisici. Preparati un mazzo di magie!",
    "Il vento nei boschi dei sigilli sussurra che le scelte più difficili spesso celano le ricompense migliori."
  ],
  en: [
    "Did you know that the Phoenix Hatchling was born from primordial fire and rises stronger every time it is defeated?",
    "Combo cards like 'Arcane Barrage' or 'Double Strike' deal immense damage if you have accumulated enough mana.",
    "It is said that in later chapters, bosses immune to physical damage are hidden. Keep a deck of spells ready!",
    "The wind in the woods of seals whispers that the hardest choices often hide the best rewards."
  ]
};

const EXPRESSIONS = {
  idle: "/assets/mascot/nyx_idle.png?v=2",
  happy: "/assets/mascot/nyx_happy.png?v=2",
  playful: "/assets/mascot/nyx_playful.png?v=2",
  curious: "/assets/mascot/nyx_curious.png?v=2",
  determined: "/assets/mascot/nyx_determined.png?v=2"
};

// Web Audio API Synthesizer to create premium UI audio indicators
const playSynthSound = (type = "chime") => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    
    if (type === "chime") {
      const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6 arpeggio
      notes.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + index * 0.08);
        gain.gain.setValueAtTime(0.08, ctx.currentTime + index * 0.08);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + index * 0.08 + 0.4);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + index * 0.08);
        osc.stop(ctx.currentTime + index * 0.08 + 0.5);
      });
    } else if (type === "levelup") {
      const notes = [261.63, 329.63, 392.00, 523.25, 659.25, 783.99, 1046.50]; // C4 to C6 chord fanfare
      notes.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + index * 0.06);
        gain.gain.setValueAtTime(0.12, ctx.currentTime + index * 0.06);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + index * 0.06 + 1.0);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + index * 0.06);
        osc.stop(ctx.currentTime + index * 0.06 + 1.2);
      });
    } else if (type === "success") {
      const notes = [523.25, 783.99];
      notes.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + index * 0.1);
        gain.gain.setValueAtTime(0.1, ctx.currentTime + index * 0.1);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + index * 0.1 + 0.3);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + index * 0.1);
        osc.stop(ctx.currentTime + index * 0.1 + 0.4);
      });
    } else if (type === "error") {
      const notes = [220.00, 196.00]; // Disappointment sound
      notes.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(freq, ctx.currentTime + index * 0.12);
        gain.gain.setValueAtTime(0.06, ctx.currentTime + index * 0.12);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + index * 0.12 + 0.5);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + index * 0.12);
        osc.stop(ctx.currentTime + index * 0.12 + 0.6);
      });
    }
  } catch (e) {
    console.warn("Web Audio API warning: ", e);
  }
};

export default function NyxMascot({ forceLang }) {
  const gameCtx = useContext(GameContext);
  const game = gameCtx?.game;

  // Visual/Expression States
  const [expression, setExpression] = useState("idle");
  const [bubbleText, setBubbleText] = useState("");
  const [showBubble, setShowBubble] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Interaction States
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeView, setActiveView] = useState("main"); // "main" | "riddle" | "oracle" | "secrets"

  // Positioning State
  const [placement, setPlacement] = useState(() => {
    return localStorage.getItem("nyx_placement") || "right";
  });

  // Affinity States (persisted)
  const [affinityLevel, setAffinityLevel] = useState(() => {
    return parseInt(localStorage.getItem("nyx_affinity_level") || "1", 10);
  });
  const [affinityExp, setAffinityExp] = useState(() => {
    return parseInt(localStorage.getItem("nyx_affinity_exp") || "0", 10);
  });

  // Physical mascot animations
  const [isSpinning, setIsSpinning] = useState(false);
  const [isJumping, setIsJumping] = useState(false);
  const [isShaking, setIsShaking] = useState(false);

  // Particles system state
  const [particles, setParticles] = useState([]);

  // Riddle sub-states
  const [currentRiddle, setCurrentRiddle] = useState(0);
  const [riddleSolved, setRiddleSolved] = useState(null); // null | "correct" | "wrong"
  const [selectedOption, setSelectedOption] = useState(null);
  const [isRiddleOnCooldown, setIsRiddleOnCooldown] = useState(false);

  // Oracle sub-states
  const [drawnCardId, setDrawnCardId] = useState(null);
  const [isFlipping, setIsFlipping] = useState(false);

  // Inactivity State
  const [isUserInactive, setIsUserInactive] = useState(false);
  const lastActivityTimeRef = useRef(Date.now());

  const lastHpRef = useRef(game?.character?.hp || 0);
  const lastLevelRef = useRef(game?.character?.level || 0);
  const bubbleTimeoutRef = useRef(null);

  const getUiLang = () => {
    if (forceLang) return forceLang.startsWith("it") ? "it" : "en";
    if (game?.language) return game.language.startsWith("it") ? "it" : "en";
    const storedUi = localStorage.getItem("ac_ui_lang");
    if (storedUi) return storedUi.startsWith("it") ? "it" : "en";
    const storedLanding = localStorage.getItem("ac_lang");
    if (storedLanding) return storedLanding.startsWith("it") ? "it" : "en";
    return "it";
  };
  const lang = getUiLang();
  const quotes = NYX_QUOTES[lang];

  // XP progression scale
  const getXpNeeded = (lvl) => lvl * 150;
  const xpNeeded = getXpNeeded(affinityLevel);

  const drawnCard = drawnCardId !== null ? TAROT_CARDS[lang][drawnCardId] : null;

  // Helper to trigger a speech bubble with specific expression
  const speak = (text, exp = "idle", duration = 6000) => {
    if (bubbleTimeoutRef.current) {
      clearTimeout(bubbleTimeoutRef.current);
    }
    setBubbleText(text);
    setExpression(exp);
    setShowBubble(true);

    bubbleTimeoutRef.current = setTimeout(() => {
      setShowBubble(false);
      setExpression("idle");
    }, duration);
  };

  // Particle Spawner
  const spawnParticles = (type, count = 10) => {
    const list = [];
    const colors = type === "heart" 
      ? ["#ef4444", "#f43f5e", "#ec4899", "#d946ef"] 
      : ["#cfb53b", "#e5cc5b", "#38bdf8", "#a855f7", "#10b981"];
    
    for (let i = 0; i < count; i++) {
      list.push({
        id: Math.random().toString(),
        x: Math.random() * 60 - 30,
        y: Math.random() * 40 - 20,
        vx: (Math.random() - 0.5) * 3,
        vy: -Math.random() * 3 - 2.5,
        type: type === "heart" ? "❤️" : Math.random() > 0.5 ? "✨" : "⭐",
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 10 + 10,
        opacity: 1,
        scale: 1,
        rotation: Math.random() * 360
      });
    }
    setParticles(prev => [...prev, ...list]);
  };

  // Particle Physics Update Loop
  useEffect(() => {
    if (particles.length === 0) return;
    const interval = setInterval(() => {
      setParticles(prev => 
        prev
          .map(p => ({
            ...p,
            x: p.x + p.vx,
            y: p.y + p.vy,
            vy: p.vy + 0.05,
            opacity: p.opacity - 0.04,
            scale: p.scale - 0.02,
            rotation: p.rotation + p.vx * 2
          }))
          .filter(p => p.opacity > 0)
      );
    }, 30);
    return () => clearInterval(interval);
  }, [particles]);

  // Affinity increaser helper
  const gainAffinity = (amount) => {
    setAffinityExp(prevExp => {
      let currentLvl = affinityLevel;
      let nextExp = prevExp + amount;
      let needed = getXpNeeded(currentLvl);

      if (nextExp >= needed) {
        while (nextExp >= needed) {
          nextExp -= needed;
          currentLvl += 1;
          needed = getXpNeeded(currentLvl);
        }
        
        setAffinityLevel(currentLvl);
        localStorage.setItem("nyx_affinity_level", currentLvl.toString());
        localStorage.setItem("nyx_affinity_exp", nextExp.toString());
        
        // Trigger Level-up events
        setIsJumping(true);
        setTimeout(() => setIsJumping(false), 600);
        spawnParticles("sparkle", 25);
        playSynthSound("levelup");
        
        const levelUpMsg = lang === "it"
          ? `Favoloso! Il nostro legame si è rafforzato! Abbiamo raggiunto il Livello d'Affinità ${currentLvl}! La mia magia è potenziata!`
          : `Fabulous! Our bond has strengthened! We reached Affinity Level ${currentLvl}! My magic is enhanced!`;
        speak(levelUpMsg, "happy", 7500);
        
        return nextExp;
      } else {
        localStorage.setItem("nyx_affinity_exp", nextExp.toString());
        return nextExp;
      }
    });
  };

  // Inactivity Detection Hook
  useEffect(() => {
    const handleActivity = () => {
      lastActivityTimeRef.current = Date.now();
      if (isUserInactive) {
        setIsUserInactive(false);
        setExpression("idle");
      }
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    
    const checkInterval = setInterval(() => {
      const idleTime = Date.now() - lastActivityTimeRef.current;
      if (idleTime > 45000 && !isUserInactive && !menuOpen && !showBubble && !isCollapsed) {
        setIsUserInactive(true);
        const idleText = lang === "it"
          ? "Zzz... Oh! Scusa, mi ero quasi addormentata. L'energia arcana a volte concilia il sonno... C'è qualche avventura in vista?"
          : "Zzz... Oh! Sorry, I almost dozed off. Arcane energy sometimes makes me sleepy... Any adventure in sight?";
        speak(idleText, "playful", 8000);
      }
    }, 5000);

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      clearInterval(checkInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserInactive, menuOpen, showBubble, isCollapsed, lang]);

  // Welcome message on first mount
  useEffect(() => {
    let welcomeText = "";
    const pathname = window.location.pathname;
    
    if (pathname === "/" || pathname === "/index.html") {
      const list = lang === "it"
        ? [
            "Benvenuto, viandante! Sei pronto a scrivere la tua leggenda in mondi scritti dall'IA?",
            "Ciao! Sono Nyx, la tua Guida Arcana. Sfidami a un indovinello o pesca un tarocco per iniziare!",
            "Registrati subito per creare il tuo eroe e iniziare la tua prima storia!"
          ]
        : [
            "Welcome, traveler! Are you ready to write your legend in AI-scripted worlds?",
            "Hello! I am Nyx, your Arcane Guide. Challenge me to a riddle or draw a tarot card to get started!",
            "Sign up now to create your hero and start your first story!"
          ];
      welcomeText = list[Math.floor(Math.random() * list.length)];
    } else if (pathname.includes("/menu")) {
      const list = lang === "it" 
        ? ["Bentornato! Seleziona un'avventura per continuare o creane una nuova!", "Pronto per un nuovo viaggio nelle infinite storie?"]
        : ["Welcome back! Select an adventure to continue or create a new one!", "Ready for a new journey through infinite stories?"];
      welcomeText = list[Math.floor(Math.random() * list.length)];
    } else if (pathname.includes("/create")) {
      const list = lang === "it"
        ? ["Chi sarai in questo nuovo mondo? Scegli il tuo nome, classe e ambientazione!", "Diamo vita a una nuova leggenda! Scegli con cura la tua classe."]
        : ["Who will you be in this new world? Choose your name, class, and setting!", "Let's forge a new legend! Choose your class with care."];
      welcomeText = list[Math.floor(Math.random() * list.length)];
    } else {
      const list = quotes.welcome;
      welcomeText = list[Math.floor(Math.random() * list.length)];
    }
    
    speak(welcomeText, "happy");

    return () => {
      if (bubbleTimeoutRef.current) clearTimeout(bubbleTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React dynamically to game changes
  useEffect(() => {
    if (!game || isCollapsed) return;

    const currentHp = game.character?.hp || 0;
    const maxHp = game.character?.max_hp || 100;
    const currentLevel = game.character?.level || 0;
    const inBattle = !!game.battle_state?.active;
    const gold = game.character?.gold || 0;

    // 1. Level up reaction
    if (currentLevel > lastLevelRef.current && lastLevelRef.current > 0) {
      setMenuOpen(false); // Alert overrides menu
      const congrats = lang === "it"
        ? `Incredibile! Sei salito al livello ${currentLevel}! Continua così!`
        : `Incredible! You leveled up to level ${currentLevel}! Keep it up!`;
      speak(congrats, "happy", 7000);
      lastLevelRef.current = currentLevel;
      lastHpRef.current = currentHp;
      return;
    }
    lastLevelRef.current = currentLevel;

    // 2. Battle state transition
    if (inBattle) {
      setMenuOpen(false);
      setExpression("determined");
      return;
    }

    // 3. Health alert reaction
    if (currentHp / maxHp < 0.3 && currentHp > 0) {
      if (lastHpRef.current / maxHp >= 0.3 || Math.random() < 0.2) {
        setMenuOpen(false);
        const warning = quotes.lowHp[Math.floor(Math.random() * quotes.lowHp.length)];
        speak(warning, "determined", 7000);
      }
      lastHpRef.current = currentHp;
      return;
    }
    lastHpRef.current = currentHp;

    // 4. Excess gold reaction
    if (gold >= 1000 && Math.random() < 0.15) {
      setMenuOpen(false);
      const goldQuote = quotes.highGold[Math.floor(Math.random() * quotes.highGold.length)];
      speak(goldQuote, "playful", 6000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, isCollapsed]);

  // Click handler (Toggles radial/action menu)
  const handleClick = () => {
    if (isCollapsed) {
      setIsCollapsed(false);
      return;
    }
    setMenuOpen(prev => !prev);
    setActiveView("main");
    setShowBubble(false);
    playSynthSound("chime");
  };

  // Advice Action
  const handleGiveAdvice = () => {
    let quote = "";
    let exp = "curious";
    const pathname = window.location.pathname;

    if (!game) {
      if (pathname === "/" || pathname === "/index.html") {
        const list = lang === "it"
          ? [
              "Crea un account per iniziare a giocare! Potrai scegliere tra varie classi come Mago, Guerriero o Ladro.",
              "Le storie sono generate in tempo reale dall'intelligenza artificiale in base alle tue decisioni!",
              "Puoi sbloccare e collezionare carte di alleati, oggetti ed abilità durante il cammino. Unisciti a noi!"
            ]
          : [
              "Create an account to start playing! You can choose from classes like Mage, Warrior, or Rogue.",
              "Stories are generated in real-time by artificial intelligence based on your choices!",
              "You can unlock and collect cards of allies, objects, and abilities along the way. Join us!"
            ];
        quote = list[Math.floor(Math.random() * list.length)];
        exp = "happy";
      } else if (pathname.includes("/menu")) {
        const list = lang === "it"
          ? [
              "Seleziona una partita salvata per continuare, oppure clicca su 'Nuova Avventura'!",
              "La Hall of Stories raccoglie le imprese della nostra community!"
            ]
          : [
              "Select a saved game to continue, or click on 'New Adventure'!",
              "The Hall of Stories collects the sagas of our community!"
            ];
        quote = list[Math.floor(Math.random() * list.length)];
        exp = "happy";
      } else if (pathname.includes("/create")) {
        const list = lang === "it"
          ? [
              "Il tuo nome sarà leggendario! La classe determina le tue abilità e carte.",
              "Scegli con cura l'ambientazione per dare inizio al tuo viaggio arcano."
            ]
          : [
              "Your name will be legendary! The class determines your skills and cards.",
              "Choose the setting carefully to start your arcane journey."
            ];
        quote = list[Math.floor(Math.random() * list.length)];
        exp = "curious";
      } else {
        quote = lang === "it" ? "Sono qui per guidarti!" : "I am here to guide you!";
      }
    } else {
      const currentHp = game?.character?.hp || 0;
      const maxHp = game?.character?.max_hp || 100;
      const inBattle = !!game?.battle_state?.active;
      const gold = game?.character?.gold || 0;

      if (inBattle) {
        const list = lang === "it"
          ? [
              "Tieni d'occhio il mana prima di giocare carte costose!",
              "Gli alleati in prima linea assorbono i danni fisici, proteggili!",
              "Se le difese nemiche sono invalicabili, usa attacchi magici diretti!"
            ]
          : [
              "Keep an eye on your mana before playing expensive cards!",
              "Frontline allies absorb physical damage, protect them!",
              "If enemy defenses are impenetrable, use direct magical attacks!"
            ];
        quote = list[Math.floor(Math.random() * list.length)];
        exp = "determined";
      } else if (currentHp / maxHp < 0.3) {
        quote = lang === "it"
          ? "Attenzione, salute critica! Consiglio vivamente di usare una Pozione di cura!"
          : "Attention, critical health! I strongly suggest using a Healing potion!";
        exp = "determined";
      } else if (gold >= 1000) {
        quote = lang === "it"
          ? "Che bel bottino d'oro! Fai un salto al Negozio per alleati migliori o carte forti!"
          : "What a nice stash of gold! Visit the Shop for better allies or strong cards!";
        exp = "playful";
      } else {
        const list = lang === "it"
          ? [
              "Distribuisci i punti attributo quando sali di livello per potenziarti!",
              "Evolvi la tua classe per sbloccare nuove ramificazioni e abilità speciali.",
              "Esplora la mappa per scoprire tesori celati e incontri interessanti."
            ]
          : [
              "Allocate your attribute points when leveling up to grow stronger!",
              "Evolve your class to unlock new branches and special skills.",
              "Explore the map to discover hidden treasures and interesting encounters."
            ];
        quote = list[Math.floor(Math.random() * list.length)];
        exp = "happy";
      }
    }

    speak(quote, exp, 7000);
    setMenuOpen(false);
  };

  // Pet Action (With 30 seconds cooldown for XP farming protection)
  const handlePetNyx = () => {
    const now = Date.now();
    const lastPetStr = localStorage.getItem("nyx_last_pet_time") || "0";
    const lastPet = parseInt(lastPetStr, 10);
    
    setIsSpinning(true);
    setTimeout(() => setIsSpinning(false), 600);
    spawnParticles("heart", 12);
    playSynthSound("chime");
    
    if (now - lastPet < 30000) {
      // Cooldown active
      const cooldownReactions = lang === "it"
        ? [
            "Grazie, avventuriero! Ma non viziarmi troppo, dobbiamo restare vigili sulla via.",
            "Hihi, mi fa il solletico! Rimaniamo però concentrati sul nostro cammino.",
            "Così mi distrai! Pensiamo alla nostra avventura."
          ]
        : [
            "Thank you, adventurer! But don't spoil me too much, we must stay alert on the path.",
            "Hehe, that tickles! But let's stay focused on our journey.",
            "You're distracting me! Let's think about our adventure."
          ];
      const quote = cooldownReactions[Math.floor(Math.random() * cooldownReactions.length)];
      speak(quote, "playful", 5000);
      setMenuOpen(false);
      return;
    }
    
    localStorage.setItem("nyx_last_pet_time", now.toString());
    const reactions = PET_REACTIONS[lang];
    const quote = reactions[Math.floor(Math.random() * reactions.length)];
    speak(quote, "happy", 6000);
    gainAffinity(15);
    setMenuOpen(false);
  };

  // Riddle Solver (with 1 min cooldown check for XP)
  const handleAnswerRiddle = (idx) => {
    const riddle = RIDDLES[lang][currentRiddle];
    setSelectedOption(idx);
    
    if (idx === riddle.answer) {
      setRiddleSolved("correct");
      setIsJumping(true);
      setTimeout(() => setIsJumping(false), 600);
      spawnParticles("sparkle", 15);
      playSynthSound("success");

      const now = Date.now();
      const lastRiddleStr = localStorage.getItem("nyx_last_riddle_time") || "0";
      const lastRiddle = parseInt(lastRiddleStr, 10);

      if (now - lastRiddle >= 60000) {
        gainAffinity(30);
        localStorage.setItem("nyx_last_riddle_time", now.toString());
      } else {
        setIsRiddleOnCooldown(true);
      }
    } else {
      setRiddleSolved("wrong");
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 400);
      setExpression("curious");
      playSynthSound("error");
    }
  };

  // Tarot Card Draw (With 2 minutes cooldown)
  const handleDrawCard = () => {
    const now = Date.now();
    const lastTarotStr = localStorage.getItem("nyx_last_tarot_time") || "0";
    const lastTarot = parseInt(lastTarotStr, 10);

    if (now - lastTarot < 120000) {
      playSynthSound("error");
      const quote = lang === "it"
        ? "I tarocchi hanno già parlato. Non forzare il destino prima del tempo!"
        : "The tarot has already spoken. Do not force destiny before its time!";
      speak(quote, "curious", 5500);
      setMenuOpen(false);
      return;
    }

    setIsFlipping(true);
    playSynthSound("chime");
    
    setTimeout(() => {
      const cardList = TAROT_CARDS[lang];
      const randomIdx = Math.floor(Math.random() * cardList.length);
      setDrawnCardId(randomIdx);
      setIsFlipping(false);
      
      playSynthSound("success");
      spawnParticles("sparkle", 12);
      setExpression("happy");
      gainAffinity(10);
      localStorage.setItem("nyx_last_tarot_time", Date.now().toString());
    }, 600);
  };

  // Placement toggler
  const togglePlacement = () => {
    const next = placement === "right" ? "left" : "right";
    setPlacement(next);
    localStorage.setItem("nyx_placement", next);
    playSynthSound("chime");
  };

  // Main menu renderer
  const renderMainMenu = () => {
    return (
      <div className="flex flex-col gap-3">
        <div className="border-b border-[#cfb53b]/30 pb-2">
          <h3 className="font-headings text-[#cfb53b] text-sm md:text-base font-bold tracking-wide flex items-center gap-1.5">
            <Sparkles size={16} className="text-[#cfb53b] animate-pulse" />
            Nyx — {lang === "it" ? "Guida Arcana" : "Arcane Guide"}
          </h3>
        </div>

        {/* Affinity Level */}
        <div className="bg-black/40 rounded p-2 border border-[#cfb53b]/10">
          <div className="flex justify-between items-center text-[10px] md:text-xs text-[#cfb53b] mb-1 font-semibold uppercase tracking-wider">
            <span>{lang === "it" ? "Affinità Nyx" : "Nyx Affinity"}</span>
            <span>{lang === "it" ? `Liv. ${affinityLevel}` : `Lvl ${affinityLevel}`}</span>
          </div>
          <div className="w-full h-1.5 bg-black/60 rounded-full overflow-hidden border border-[#cfb53b]/20">
            <div 
              style={{ width: `${(affinityExp / xpNeeded) * 100}%` }} 
              className="h-full bg-gradient-to-r from-[#cfb53b] to-[#e5cc5b] transition-all duration-500 shadow-[0_0_8px_rgba(207,181,59,0.5)]" 
            />
          </div>
          <div className="text-[9px] text-[#cbd5e1]/60 mt-0.5 text-right font-body">
            {affinityExp}/{xpNeeded} EXP
          </div>
        </div>

        {/* Actions grid */}
        <div className="grid grid-cols-2 gap-2 text-xs font-body">
          <button
            onClick={handleGiveAdvice}
            className="flex flex-col items-center justify-center p-2.5 bg-black/30 hover:bg-[#cfb53b]/15 border border-[#cfb53b]/20 rounded text-[#cbd5e1] hover:text-[#cfb53b] transition-all duration-200"
          >
            <MessageSquare size={16} className="mb-1 text-sky-400" />
            <span className="font-medium text-[10px] md:text-xs">
              {lang === "it" ? "Consiglio" : "Advice"}
            </span>
          </button>
          
          <button
            onClick={handlePetNyx}
            className="flex flex-col items-center justify-center p-2.5 bg-black/30 hover:bg-[#cfb53b]/15 border border-[#cfb53b]/20 rounded text-[#cbd5e1] hover:text-[#cfb53b] transition-all duration-200"
          >
            <Heart size={16} className="mb-1 text-rose-400" />
            <span className="font-medium text-[10px] md:text-xs">
              {lang === "it" ? "Accarezza" : "Pet"}
            </span>
          </button>
          
          <button
            onClick={() => {
              setActiveView("riddle");
              setRiddleSolved(null);
              setSelectedOption(null);
              setCurrentRiddle(Math.floor(Math.random() * RIDDLES[lang].length));
              playSynthSound("chime");
              
              const now = Date.now();
              const lastRiddleStr = localStorage.getItem("nyx_last_riddle_time") || "0";
              const lastRiddle = parseInt(lastRiddleStr, 10);
              setIsRiddleOnCooldown(now - lastRiddle < 60000);
            }}
            className="flex flex-col items-center justify-center p-2.5 bg-black/30 hover:bg-[#cfb53b]/15 border border-[#cfb53b]/20 rounded text-[#cbd5e1] hover:text-[#cfb53b] transition-all duration-200"
          >
            <HelpCircle size={16} className="mb-1 text-amber-400" />
            <span className="font-medium text-[10px] md:text-xs">
              {lang === "it" ? "Indovinello" : "Riddle"}
            </span>
          </button>
          
          <button
            onClick={() => {
              setActiveView("oracle");
              setDrawnCardId(null);
              setIsFlipping(false);
              playSynthSound("chime");
            }}
            className="flex flex-col items-center justify-center p-2.5 bg-black/30 hover:bg-[#cfb53b]/15 border border-[#cfb53b]/20 rounded text-[#cbd5e1] hover:text-[#cfb53b] transition-all duration-200"
          >
            <Compass size={16} className="mb-1 text-indigo-400" />
            <span className="font-medium text-[10px] md:text-xs">
              {lang === "it" ? "Oracolo" : "Oracle"}
            </span>
          </button>
        </div>

        {/* Secrets Button (unlocked at Lvl 3) */}
        {affinityLevel >= 3 && (
          <button
            onClick={() => {
              setActiveView("secrets");
              playSynthSound("chime");
            }}
            className="w-full mt-1 py-1.5 bg-gradient-to-r from-[#cfb53b]/10 to-[#cfb53b]/20 hover:from-[#cfb53b]/20 hover:to-[#cfb53b]/30 border border-[#cfb53b]/40 rounded text-[#cfb53b] text-center text-[10px] font-headings uppercase tracking-wider font-semibold transition-all shadow-md"
          >
            📜 {lang === "it" ? "Segreti Arcani" : "Arcane Secrets"}
          </button>
        )}

        {/* Position control */}
        <div className="border-t border-[#cfb53b]/20 pt-2 flex justify-between items-center text-[10px] text-[#cbd5e1]/70">
          <span>{lang === "it" ? "Sposta guida:" : "Move guide:"}</span>
          <button
            onClick={togglePlacement}
            className="flex items-center gap-1 py-0.5 px-2 bg-black/40 hover:bg-[#cfb53b]/10 border border-[#cfb53b]/30 rounded transition-colors text-[#cfb53b]"
          >
            <ArrowLeftRight size={10} />
            <span>{placement === "right" ? (lang === "it" ? "Sinistra" : "Left") : (lang === "it" ? "Destra" : "Right")}</span>
          </button>
        </div>
      </div>
    );
  };

  // Riddle game renderer
  const renderRiddleView = () => {
    const riddle = RIDDLES[lang][currentRiddle];
    return (
      <div className="flex flex-col gap-3 font-body">
        <div className="border-b border-[#cfb53b]/30 pb-1.5 flex justify-between items-center">
          <span className="font-headings text-[#cfb53b] text-xs font-bold uppercase tracking-wider flex items-center gap-1">
            <HelpCircle size={14} />
            {lang === "it" ? "Sfida della Sfinge" : "Riddle of the Sphinx"}
          </span>
          <button
            onClick={() => {
              setActiveView("main");
              playSynthSound("chime");
            }}
            className="text-[10px] text-[#cfb53b] hover:underline"
          >
            {lang === "it" ? "Indietro" : "Back"}
          </button>
        </div>

        <div className="text-xs text-slate-200 bg-black/30 p-2.5 rounded border border-slate-800 leading-relaxed font-light">
          {riddle.question}
        </div>

        <div className="flex flex-col gap-1.5 text-xs">
          {riddle.options.map((opt, idx) => {
            let btnStyle = "bg-black/30 hover:bg-[#cfb53b]/10 border-[#cfb53b]/30 text-slate-300";
            
            if (riddleSolved !== null) {
              if (idx === riddle.answer) {
                btnStyle = "bg-emerald-950/80 border-emerald-500 text-emerald-300 shadow-[0_0_8px_rgba(16,185,129,0.2)]";
              } else if (selectedOption === idx) {
                btnStyle = "bg-rose-950/80 border-rose-500 text-rose-300";
              } else {
                btnStyle = "bg-black/10 border-slate-800 text-slate-500 opacity-60";
              }
            }

            return (
              <button
                key={idx}
                disabled={riddleSolved !== null}
                onClick={() => handleAnswerRiddle(idx)}
                className={`w-full py-2 px-3 border rounded text-left transition-all duration-200 ${btnStyle}`}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {riddleSolved !== null && (
          <div className={`text-[11px] p-2 rounded border leading-relaxed animate-fade-in ${
            riddleSolved === "correct" 
              ? "bg-emerald-950/40 border-emerald-800/40 text-emerald-300"
              : "bg-rose-950/40 border-rose-800/40 text-rose-300"
          }`}>
            <p className="font-semibold mb-0.5">
              {riddleSolved === "correct" 
                ? (isRiddleOnCooldown 
                   ? (lang === "it" ? "✨ Corretto! (Affinità già ottenuta)" : "✨ Correct! (Affinity already claimed)")
                   : (lang === "it" ? "✨ Corretto! (+30 Affinità)" : "✨ Correct! (+30 Affinity)"))
                : (lang === "it" ? "❌ Errato..." : "❌ Incorrect...")}
            </p>
            <p className="text-[10px] text-slate-300 leading-normal">{riddle.rewardText}</p>
          </div>
        )}

        {riddleSolved !== null && (
          <div className="flex justify-end gap-2 mt-1">
            <button
              onClick={() => {
                setRiddleSolved(null);
                setSelectedOption(null);
                let nextIdx = currentRiddle;
                while (nextIdx === currentRiddle && RIDDLES[lang].length > 1) {
                  nextIdx = Math.floor(Math.random() * RIDDLES[lang].length);
                }
                setCurrentRiddle(nextIdx);
                playSynthSound("chime");
                
                const now = Date.now();
                const lastRiddleStr = localStorage.getItem("nyx_last_riddle_time") || "0";
                const lastRiddle = parseInt(lastRiddleStr, 10);
                setIsRiddleOnCooldown(now - lastRiddle < 60000);
              }}
              className="px-2.5 py-1 text-[10px] uppercase font-bold tracking-wider text-slate-900 bg-[#cfb53b] hover:bg-[#e5cc5b] rounded transition-colors"
            >
              {lang === "it" ? "Prossimo" : "Next"}
            </button>
            <button
              onClick={() => {
                setActiveView("main");
                playSynthSound("chime");
              }}
              className="px-2.5 py-1 text-[10px] uppercase font-bold tracking-wider border border-[#cfb53b] text-[#cfb53b] hover:bg-[#cfb53b]/10 rounded transition-colors"
            >
              {lang === "it" ? "Menu" : "Menu"}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Tarot cards draw renderer
  const renderOracleView = () => {
    return (
      <div className="flex flex-col gap-3 items-center font-body">
        <div className="w-full border-b border-[#cfb53b]/30 pb-1.5 flex justify-between items-center">
          <span className="font-headings text-[#cfb53b] text-xs font-bold uppercase tracking-wider flex items-center gap-1">
            <Compass size={14} />
            {lang === "it" ? "Oracolo dei Tarocchi" : "Tarot Oracle"}
          </span>
          <button
            onClick={() => {
              setActiveView("main");
              playSynthSound("chime");
            }}
            className="text-[10px] text-[#cfb53b] hover:underline"
          >
            {lang === "it" ? "Indietro" : "Back"}
          </button>
        </div>

        {/* The Card Item */}
        <div className="card-container w-[110px] h-[160px] cursor-pointer my-1 select-none">
          <div className={`card-inner w-full h-full relative rounded-md border border-[#cfb53b]/60 ${
            drawnCard !== null ? "card-flipped" : ""
          }`}>
            {/* Front of Card */}
            <div className="card-front absolute inset-0 bg-gradient-to-b from-[#1a1a1d] to-[#0a0a0c] flex flex-col items-center justify-between p-2 rounded-md shadow-2xl">
              <div className="text-[9px] text-[#cfb53b]/50 tracking-[0.1em] font-headings uppercase">
                {lang === "it" ? "Tarocco Arcano" : "Arcane Tarot"}
              </div>
              <div className="text-4xl my-1 animate-pulse">
                {drawnCard?.icon}
              </div>
              <div className="w-full text-center border-t border-[#cfb53b]/30 pt-1">
                <span className="font-headings text-[11px] font-bold text-[#cfb53b] tracking-wider block">
                  {drawnCard?.name}
                </span>
              </div>
            </div>

            {/* Back of Card */}
            <div className="card-back absolute inset-0 bg-[#0a0a0c] bg-[radial-gradient(circle_at_center,_#2c1a4d_0%,_#0a0a0c_75%)] flex flex-col items-center justify-center p-2 rounded-md border border-[#cfb53b]/40 hover:border-[#cfb53b] transition-colors">
              <div className="w-full h-full border border-[#cfb53b]/20 flex flex-col items-center justify-center rounded">
                <Sparkles size={24} className="text-[#cfb53b] animate-pulse mb-1" />
                <span className="text-[8px] text-[#cfb53b]/50 tracking-[0.1em] font-headings uppercase text-center">
                  {lang === "it" ? "Pesca un Destino" : "Draw Destiny"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {drawnCard === null ? (
          <div className="text-center text-[11px] text-slate-400 max-w-[200px] leading-relaxed font-light">
            {lang === "it" 
              ? "Pesca una carta per ricevere una predizione giornaliera (+10 Affinità)." 
              : "Draw a card to receive a daily prediction (+10 Affinity)."}
            <button
              disabled={isFlipping}
              onClick={handleDrawCard}
              className={`w-full mt-3 py-1.5 uppercase font-headings font-bold text-[10px] tracking-wider text-slate-900 bg-[#cfb53b] hover:bg-[#e5cc5b] rounded shadow-md transition-colors ${
                isFlipping ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isFlipping 
                ? (lang === "it" ? "Mescolando..." : "Shuffling...")
                : (lang === "it" ? "Pesca Carta" : "Draw Card")}
            </button>
          </div>
        ) : (
          <div className="text-center text-[11px] bg-black/30 p-2 rounded border border-[#cfb53b]/20 animate-fade-in leading-relaxed font-light">
            <p className="font-semibold text-[#cfb53b] mb-1 font-headings text-[10px] uppercase tracking-wider">
              {lang === "it" ? "Predizione:" : "Prediction:"}
            </p>
            <p className="text-slate-300 text-[10px] leading-normal">{drawnCard.desc}</p>
            <button
              onClick={() => {
                setActiveView("main");
                playSynthSound("chime");
              }}
              className="mt-3 text-[10px] text-[#cfb53b] hover:underline block mx-auto"
            >
              {lang === "it" ? "Torna al Menu" : "Back to Menu"}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Arcane Secrets View (Unlocked at Lvl 3)
  const renderSecretsView = () => {
    const secretList = LORE_SECRETS[lang];
    const secret = secretList[affinityLevel % secretList.length];
    
    return (
      <div className="flex flex-col gap-3 font-body">
        <div className="border-b border-[#cfb53b]/30 pb-1.5 flex justify-between items-center">
          <span className="font-headings text-[#cfb53b] text-xs font-bold uppercase tracking-wider flex items-center gap-1">
            <Sparkles size={14} className="text-emerald-400 animate-pulse" />
            {lang === "it" ? "Segreti Arcani" : "Arcane Secrets"}
          </span>
          <button
            onClick={() => {
              setActiveView("main");
              playSynthSound("chime");
            }}
            className="text-[10px] text-[#cfb53b] hover:underline"
          >
            {lang === "it" ? "Indietro" : "Back"}
          </button>
        </div>

        <div className="text-xs text-[#cbd5e1] bg-black/40 p-3 rounded border border-[#cfb53b]/20 leading-relaxed font-light italic text-center">
          "{secret}"
        </div>

        <button
          onClick={() => {
            setActiveView("main");
            playSynthSound("chime");
          }}
          className="w-full mt-2 py-1 bg-black/50 border border-[#cfb53b]/30 hover:bg-[#cfb53b]/10 rounded text-[10px] text-[#cfb53b] uppercase tracking-wider font-headings transition-colors"
        >
          {lang === "it" ? "Chiudi" : "Close"}
        </button>
      </div>
    );
  };

  const imageSrc = EXPRESSIONS[expression] || EXPRESSIONS.idle;

  const getAvatarClassName = () => {
    let aura = "drop-shadow-[0_0_15px_rgba(168,85,247,0.4)]";
    if (affinityLevel >= 8) {
      aura = "drop-shadow-[0_0_25px_rgba(207,181,59,0.95)]"; // Golden aura
    } else if (affinityLevel >= 5) {
      aura = "drop-shadow-[0_0_20px_rgba(236,72,153,0.85)]"; // Pink/Magenta aura
    } else if (affinityLevel >= 3) {
      aura = "drop-shadow-[0_0_15px_rgba(56,189,248,0.75)]"; // Blue aura
    }

    let base = `w-[96px] h-[120px] md:w-[150px] md:h-[188px] cursor-pointer transition-transform duration-300 hover:scale-105 active:scale-95 select-none ${aura}`;
    
    if (isSpinning) {
      base += " animate-nyx-spin";
    } else if (isJumping) {
      base += " animate-nyx-jump";
    } else if (isShaking) {
      base += " animate-nyx-shake";
    } else {
      base += " animate-nyx-float";
    }
    return base;
  };

  if (isCollapsed) {
    return (
      <div className={`fixed bottom-20 z-50 md:bottom-6 ${placement === "right" ? "right-4" : "left-4"}`}>
        <button
          onClick={() => {
            setIsCollapsed(false);
            playSynthSound("chime");
          }}
          className="relative group p-3 bg-[#1a1a1d] hover:bg-[#cfb53b]/15 text-[#cfb53b] rounded-full border border-[#cfb53b]/40 shadow-[0_0_15px_rgba(207,181,59,0.3)] transition-all duration-300 hover:scale-110 flex items-center justify-center animate-nyx-float"
          title={lang === "it" ? "Mostra Nyx" : "Show Nyx"}
        >
          <Sparkles className="w-5 h-5" />
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-ping" />
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full" />
        </button>
        <style>{`
          @keyframes nyx-float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-6px); }
          }
          .animate-nyx-float {
            animation: nyx-float 3s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className={`fixed bottom-20 z-50 md:bottom-6 flex flex-col pointer-events-none ${
      placement === "right" ? "right-4 items-end" : "left-4 items-start"
    }`}>
      {/* Interactive Menu or Speech Bubble */}
      {menuOpen ? (
        <div 
          className="mb-4 w-[280px] md:w-[320px] bg-[#1a1a1d]/95 border-2 border-[#cfb53b] rounded-md p-4 shadow-[0_8px_32px_rgba(0,0,0,0.8)] backdrop-blur-md text-[#cbd5e1] font-body relative animate-fade-in pointer-events-auto select-none"
        >
          <button
            onClick={() => {
              setMenuOpen(false);
              playSynthSound("chime");
            }}
            className="absolute top-2.5 right-2.5 text-[#cfb53b]/60 hover:text-[#cfb53b] transition-colors"
          >
            <X size={16} />
          </button>
          
          {activeView === "main" && renderMainMenu()}
          {activeView === "riddle" && renderRiddleView()}
          {activeView === "oracle" && renderOracleView()}
          {activeView === "secrets" && renderSecretsView()}
        </div>
      ) : (
        showBubble && (
          <div 
            className="mb-3 max-w-[260px] md:max-w-[320px] bg-[#1a1a1d]/95 border border-[#cfb53b]/50 rounded-md p-3 md:p-4 shadow-[0_8px_32px_rgba(0,0,0,0.6)] backdrop-blur-md text-[#cbd5e1] text-xs md:text-sm relative animate-fade-in pointer-events-auto cursor-pointer"
            onClick={() => setShowBubble(false)}
          >
            {/* Arrow direction */}
            <div className={`absolute bottom-[-6px] w-3 h-3 bg-[#1a1a1d] border-r border-b border-[#cfb53b]/50 transform rotate-45 ${
              placement === "right" ? "right-8" : "left-8"
            }`} />
            
            <div className="flex items-start gap-2">
              <MessageSquare size={14} className="text-[#cfb53b] shrink-0 mt-0.5" />
              <p className="leading-relaxed font-body">{bubbleText}</p>
            </div>
          </div>
        )
      )}

      {/* Nyx Body Container */}
      <div className="relative group flex items-end pointer-events-auto">
        {/* Collapse Button */}
        <button
          onClick={() => {
            setIsCollapsed(true);
            setMenuOpen(false);
          }}
          className="absolute top-0 left-0 bg-black/60 hover:bg-red-500/20 text-[#cbd5e1] hover:text-red-400 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 border border-[#cfb53b]/20 z-10"
          title={lang === "it" ? "Nascondi Nyx" : "Hide Nyx"}
        >
          <X size={12} />
        </button>

        {/* Affinity Badge */}
        <div className={`absolute -top-3 bg-[#1a1a1d] border border-[#cfb53b] px-1.5 py-0.5 rounded text-[8px] font-headings text-[#cfb53b] font-bold tracking-wider shadow-[0_0_8px_rgba(207,181,59,0.4)] select-none z-10 ${
          placement === "right" ? "right-6" : "left-6"
        }`}>
          LV. {affinityLevel}
        </div>

        {/* Particles */}
        <div className="absolute inset-0 pointer-events-none z-20">
          {particles.map(p => (
            <span
              key={p.id}
              style={{
                position: "absolute",
                left: `calc(50% + ${p.x}px)`,
                top: `calc(40% + ${p.y}px)`,
                color: p.color,
                fontSize: `${p.size}px`,
                opacity: p.opacity,
                transform: `scale(${p.scale}) rotate(${p.rotation}deg)`,
                transition: "opacity 0.03s linear, transform 0.03s linear",
                textShadow: `0 0 4px ${p.color}`
              }}
            >
              {p.type}
            </span>
          ))}
        </div>

        {/* The Animated Avatar */}
        <div 
          onClick={handleClick}
          className={getAvatarClassName()}
        >
          <img 
            src={imageSrc} 
            alt="Nyx Mascot" 
            className="w-full h-full object-contain pointer-events-none select-none"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      </div>

      {/* CSS Keyframe Animations Injection */}
      <style>{`
        @keyframes nyx-float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-8px) rotate(1deg); }
        }
        .animate-nyx-float {
          animation: nyx-float 4.5s ease-in-out infinite;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out forwards;
        }

        /* Card flip styles */
        .card-container {
          perspective: 800px;
        }
        .card-inner {
          transition: transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          transform-style: preserve-3d;
        }
        .card-flipped {
          transform: rotateY(180deg);
        }
        .card-front, .card-back {
          backface-visibility: hidden;
        }
        .card-front {
          transform: rotateY(180deg);
        }

        /* Custom animations */
        @keyframes nyx-spin {
          0% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.1) rotate(180deg); }
          100% { transform: scale(1) rotate(360deg); }
        }
        .animate-nyx-spin {
          animation: nyx-spin 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }

        @keyframes nyx-jump {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-24px) scaleY(1.05); }
        }
        .animate-nyx-jump {
          animation: nyx-jump 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }

        @keyframes nyx-shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-6px); }
          40%, 80% { transform: translateX(6px); }
        }
        .animate-nyx-shake {
          animation: nyx-shake 0.4s ease-in-out forwards;
        }
      `}</style>
    </div>
  );
}
